import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FullStory } from '@fullstory/browser';
import { ElevateIcons } from '@hg-akademie-ban/ban-ui-assets/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useServerFn } from '@tanstack/react-start';
import { useState, useSyncExternalStore } from 'react';

import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '~ai-tutor/components/ui/dropdownMenu';
import { useAuth } from '~ai-tutor/lib/contexts/auth';
import { useTranslation } from '~ai-tutor/lib/i18n';
import { knowledgeProfileQueryOptions } from '~ai-tutor/lib/serverFunctions/knowledgeProfile';
import { conversationSettingsQueryOptions } from '~ai-tutor/lib/serverFunctions/settings/getConversationSettings';

import { signOut } from '../lib/auth/better-auth/authClient';
import { logoutFn } from '../lib/serverFunctions/auth';
import { useChatPersistence } from './chat/helper/useChatPersistence';
import { MaskField } from './common/MaskField';
import { KnowledgeProfileDisplay } from './knowledge/KnowledgeProfileDisplay';
import { LanguageSwitcher } from './LanguageSwitcher';
import HaufeLogo from './logo/HaufeLogo';
import HaufeLogoIcon from './logo/HaufeLogoIcon';
import LogoLumina from './logo/LogoLumina';
import { TutorUiLanguageSync } from './TutorUiLanguageSync';
import { Button } from './ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';

const unsubscribeNoop = () => {
  // Client snapshot is constant; nothing to unsubscribe
};

const subscribeNoop = () => unsubscribeNoop;
const getClientSnapshot = () => true;
const getServerSnapshot = () => false;

export default function Header() {
  const { t } = useTranslation();
  const logout = useServerFn(logoutFn);
  const navigate = useNavigate();
  // Use the rendered location so header controls stay aligned with page content
  // during pending navigations (e.g. settings → chat while the chat loader runs).
  const pathname = useRouterState({
    select: (state) => state.resolvedLocation?.pathname ?? state.location.pathname,
  });

  const { sessionId, conversationId, name, familyName, identityType } = useAuth();

  const queryClient = useQueryClient();

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogKnowledgeOpen, setDialogKnowledgeOpen] = useState(false);
  // Avoid SSR/client mismatch: sessionId often exists in the client query cache but not in SSR HTML
  const isMounted = useSyncExternalStore(subscribeNoop, getClientSnapshot, getServerSnapshot);

  const { data: conversationSettings } = useQuery({
    ...conversationSettingsQueryOptions(conversationId ?? ''),
    enabled: !!conversationId,
  });

  const { clearStorage } = useChatPersistence();

  const { data } = useQuery({
    ...knowledgeProfileQueryOptions(conversationId ?? ''),
    enabled: !!conversationId,
  });

  const handleLogout = async () => {
    FullStory('trackEvent', {
      name: 'lernassistent.btnLogout.click',
      properties: {},
    });

    setDropdownOpen(false);
    setDialogOpen(false);
    setDialogKnowledgeOpen(false);

    await logout();
    await signOut();

    queryClient.clear();
    clearStorage();

    await navigate({ href: 'https://www.haufe-akademie.de' });
  };

  const handleSettingsReset = async () => {
    if (!conversationId) return;

    FullStory('trackEvent', {
      name: 'lernassistent.settings.reset',
      properties: {},
    });
    clearStorage();
    setDialogOpen(false);
    setDropdownOpen(false);
    await navigate({ to: '/settings' });
  };

  const isChatPage = pathname === '/chat';
  const isSettingsPage = pathname === '/settings';
  const enableKnowledgeProfileLink = data?.knowledgeProfile !== undefined && isChatPage;
  const hasSettings = !!(
    conversationSettings?.goal &&
    conversationSettings.jobRole &&
    conversationSettings.experience
  );
  const enableSettingsLink = isChatPage && hasSettings;

  return (
    <>
      {!isSettingsPage && <TutorUiLanguageSync language={conversationSettings?.language} />}
      <header
        className="bg-background sticky top-0 z-10"
        style={{
          boxShadow: 'rgba(111, 1, 245, 0.1) 0px 0px 50px 0px',
          transition: 'box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <div className="container mx-auto flex max-w-7xl items-center justify-between gap-6 px-12 py-16">
          {/* Brand Section */}
          <div className="flex items-center gap-2 gap-3 transition-opacity hover:opacity-80">
            <HaufeLogo width={170} className="hidden align-baseline md:block" />
            <HaufeLogoIcon width={24} className="align-baseline md:hidden" />

            <div className="bg-border mx-1 h-12 w-px rounded md:mx-1" aria-hidden="true" />

            <div className="flex items-center gap-2 md:gap-3">
              <LogoLumina size={30} />
              <span className="text-md text-foreground">{t('nav.appTitle')}</span>
            </div>
          </div>

          {/* Navigation Links */}
          <nav className="flex shrink-0 items-center gap-2" role="navigation" aria-label="Main navigation">
            {isMounted && sessionId && (
              <div className="flex shrink-0 items-center gap-4">
                {isChatPage && (
                  <div>
                    <Dialog open={dialogKnowledgeOpen} onOpenChange={setDialogKnowledgeOpen}>
                      <DialogTrigger>
                        <Button
                          variant="ghost"
                          aria-label="Actual Learning Status"
                          disabled={!enableKnowledgeProfileLink}
                        >
                          <FontAwesomeIcon icon={ElevateIcons.solid.faUserGraduate} />
                          <div className="hidden md:block">{t('nav.actual')}</div>
                        </Button>
                      </DialogTrigger>
                      <DialogPortal>
                        <DialogOverlay />
                        <DialogContent className="flex max-h-[90vh] max-w-4xl flex-col overflow-hidden">
                          <DialogTitle>{t('nav.actual')}</DialogTitle>
                          <div className="flex-1 overflow-y-auto pr-8">
                            {data?.knowledgeProfile && (
                              <KnowledgeProfileDisplay knowledgeProfile={data.knowledgeProfile} />
                            )}
                          </div>
                          <DialogClose />
                        </DialogContent>
                      </DialogPortal>
                    </Dialog>

                    <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          className="hover:bg-accent cursor-pointer transition-all"
                          title={t('nav.settings')}
                          aria-label={t('nav.settings')}
                          disabled={!enableSettingsLink}
                        >
                          <FontAwesomeIcon className="size-4" icon={ElevateIcons.solid.faGear} />
                          <div className="hidden md:block">{t('nav.settings')}</div>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-full max-w-sm p-20">
                        <ul className="list-inside space-y-12 text-sm">
                          <li>
                            {t('header.dropdown.thema')} {conversationSettings?.topic}
                          </li>
                          <li>
                            {t('header.dropdown.lernziel')} {conversationSettings?.goal}
                          </li>
                          <li>
                            {t('header.dropdown.jobrolle')} {conversationSettings?.jobRole}
                          </li>
                        </ul>
                        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                          <DialogTrigger>
                            <Button
                              variant="secondary"
                              aria-label={t('nav.settings')}
                              className="hover:bg-accent mt-20 w-full cursor-pointer"
                            >
                              {t('header.dropdown.changeSettings')}
                            </Button>
                          </DialogTrigger>
                          <DialogPortal>
                            <DialogOverlay />
                            <DialogContent className="prose">
                              <DialogTitle>{t('header.dialog.title')}</DialogTitle>
                              <p>{t('header.dialog.description1')}</p>
                              <p>{t('header.dialog.description2')}</p>
                              <p>{t('header.dialog.description3')}</p>
                              <div className="mt-8 flex justify-start">
                                <Button
                                  onClick={handleSettingsReset}
                                  variant="secondary"
                                  className="hover:bg-accent cursor-pointer"
                                >
                                  {t('header.dialog.confirmButton')}
                                </Button>
                              </div>
                              <DialogClose />
                            </DialogContent>
                          </DialogPortal>
                        </Dialog>
                        <Button
                          variant="outline"
                          onClick={handleLogout}
                          aria-label={t('nav.logout')}
                          className="hover:bg-accent mt-12 w-full cursor-pointer"
                        >
                          {t('nav.logout')}
                        </Button>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                )}
                {isSettingsPage && <LanguageSwitcher />}
                {identityType === 'oidc' && (
                  <div>
                    <DropdownMenu open={userMenuOpen} onOpenChange={setUserMenuOpen}>
                      <DropdownMenuTrigger asChild>
                        <FontAwesomeIcon
                          className="text-brand w-24"
                          size="lg"
                          icon={ElevateIcons.solid.faUserCircle}
                        />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="p-20">
                        <div>
                          <MaskField>{`${name} ${familyName ?? ''}`.trim()}</MaskField>
                        </div>
                        <Button
                          variant="outline"
                          onClick={handleLogout}
                          aria-label={t('nav.logout')}
                          className="hover:bg-accent mt-12 w-full cursor-pointer"
                        >
                          {t('nav.logout')}
                        </Button>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                )}
              </div>
            )}
          </nav>
        </div>
      </header>
    </>
  );
}
